import { useState, useCallback, useEffect } from 'react';
import {
  CharacterData,
  createDefaultCharacter,
} from '../types/character';
import {
  createCharacter as createFirebaseCharacter,
  updateCharacter as updateFirebaseCharacter,
  getCharacter as getFirebaseCharacter,
  getUserCharacters as getFirebaseUserCharacters,
  deleteCharacter as deleteFirebaseCharacter,
  subscribeToCharacter,
  subscribeToUserCharacters,
} from '../firebase/firestore';

export interface UseFirebaseCharacterResult {
  // Character data
  character: CharacterData | null;
  characters: CharacterData[];
  
  // Loading states
  loading: boolean;
  charactersLoading: boolean;
  saving: boolean;
  
  // Error states
  error: string | null;
  
  // Character operations
  createCharacter: (characterData?: Partial<CharacterData>) => Promise<string>;
  updateCharacter: (updates: Partial<CharacterData>) => Promise<void>;
  loadCharacter: (characterId: string) => Promise<void>;
  deleteCharacter: (characterId: string) => Promise<void>;
  
  // User characters operations
  loadUserCharacters: () => Promise<void>;
  
  // Real-time subscriptions
  subscribeToCurrentCharacter: (characterId: string) => () => void;
  subscribeToUserCharactersList: () => () => void;
}

export const useFirebaseCharacter = (userId: string): UseFirebaseCharacterResult => {
  // State
  const [character, setCharacter] = useState<CharacterData | null>(null);
  const [characters, setCharacters] = useState<CharacterData[]>([]);
  const [loading, setLoading] = useState(false);
  const [charactersLoading, setCharactersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [charactersLoaded, setCharactersLoaded] = useState(false);

  // Reset charactersLoaded when userId changes (only if it actually changed)
  useEffect(() => {
    if (userId) {
      setCharactersLoaded(false);
      setCharacters([]);
    }
  }, [userId]);

  // Create character
  const createCharacter = useCallback(async (characterData?: Partial<CharacterData>): Promise<string> => {
    setLoading(true);
    setError(null);
    
    try {
      const newCharacterData = {
        ...createDefaultCharacter(),
        ...characterData,
      };
      
      // Remove id if it exists (Firestore will generate it)
      const { id, ...dataWithoutId } = newCharacterData;
      
      const characterId = await createFirebaseCharacter(userId, dataWithoutId);
      
      // Set the created character as current
      const createdCharacter = { id: characterId, ...dataWithoutId };
      setCharacter(createdCharacter);
      
      // Add the created character to the characters list
      setCharacters(prev => [...prev, createdCharacter]);
      
      return characterId;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create character';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Update character
  const updateCharacter = useCallback(async (updates: Partial<CharacterData>): Promise<void> => {
    if (!character?.id) {
      console.warn('Attempted to update character without ID, skipping update');
      return; // Return silently instead of throwing error
    }

    setSaving(true);
    setError(null);
    
    try {
      await updateFirebaseCharacter(userId, character.id, updates);
      
      // Update local state
      setCharacter(prev => prev ? { ...prev, ...updates } : null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update character';
      setError(errorMessage);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [userId, character?.id]);

  // Load character
  const loadCharacter = useCallback(async (characterId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const loadedCharacter = await getFirebaseCharacter(userId, characterId);
      setCharacter(loadedCharacter);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load character';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Delete character
  const deleteCharacter = useCallback(async (characterId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      await deleteFirebaseCharacter(userId, characterId);
      
      // Clear current character if it was deleted
      if (character?.id === characterId) {
        setCharacter(null);
      }
      
      // Remove from characters list
      setCharacters(prev => prev.filter(c => c.id !== characterId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete character';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId, character?.id]);

  // Load user characters
  const loadUserCharacters = useCallback(async (): Promise<void> => {
    if (charactersLoaded) {
      return; // Prevent multiple loads
    }
    
    setCharactersLoading(true);
    setError(null);
    
    try {
      const userCharacters = await getFirebaseUserCharacters(userId);
      setCharacters(userCharacters);
      setCharactersLoaded(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load characters';
      setError(errorMessage);
      throw err;
    } finally {
      setCharactersLoading(false);
    }
  }, [userId, charactersLoaded]);

  // Stable callback for character updates
  const handleCharacterUpdate = useCallback((updatedCharacter: CharacterData | null) => {
    setCharacter(updatedCharacter);
  }, []);

  // Subscribe to current character
  const subscribeToCurrentCharacter = useCallback((characterId: string) => {
    const unsubscribe = subscribeToCharacter(userId, characterId, handleCharacterUpdate);
    return unsubscribe;
  }, [userId, handleCharacterUpdate]);

  // Stable callback for user characters updates
  const handleCharactersUpdate = useCallback((updatedCharacters: CharacterData[]) => {
    setCharacters(updatedCharacters);
  }, []);

  // Subscribe to user characters list
  const subscribeToUserCharactersList = useCallback(() => {
    const unsubscribe = subscribeToUserCharacters(userId, handleCharactersUpdate);
    return unsubscribe;
  }, [userId, handleCharactersUpdate]);

  return {
    // Data
    character,
    characters,
    
    // Loading states
    loading,
    charactersLoading,
    saving,
    
    // Error state
    error,
    
    // Operations
    createCharacter,
    updateCharacter,
    loadCharacter,
    deleteCharacter,
    loadUserCharacters,
    
    // Subscriptions
    subscribeToCurrentCharacter,
    subscribeToUserCharactersList,
  };
};